<template>
  <div class="extrusion-proceso-form p-3 mb-2 bg-gray-50 rounded border border-gray-200">
    <el-row
      :gutter="10"
      align="bottom"
    >
      <el-col :span="5">
        <div class="label">Material resultante</div>
        <el-select
          v-model="model.MaterialResultante"
          filterable
          clearable
          placeholder="Seleccionar material"
          class="ancho_total"
          :class="{ 'input-error': faltaMaterialResultante }"
          value-key="Codigo"
          size="default"
          @change="onMaterialChange"
        >
          <el-option
            v-for="m in materialesExtrusion"
            :key="m.Codigo"
            :label="m.DescripcionInterna"
            :value="m"
          />
        </el-select>
      </el-col>
      <el-col
        v-if="!esCalculoPorGramaje"
        :span="2"
      >
        <custom-input-number
          v-model="model.EspesorResultante"
          label="Espesor (μm)"
          :settings="{
            placeholder: 'Espesor (μm)',
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
            inputClass: { 'input-error': faltaEspesor },
          }"
          @update:model-value="emitUpdate"
        />
      </el-col>
      <el-col
        v-if="!esCalculoPorGramaje"
        :span="2"
      >
        <custom-input-number
          v-model="model.DensidadResultante"
          label="Densidad (g/cm³)"
          :settings="{
            placeholder: 'g/cm³',
            min: 0,
            precision: 4,
            step: 0.0001,
            controls: false,
            inputClass: { 'input-error': faltaDensidad },
          }"
          @update:model-value="emitUpdate"
        />
      </el-col>
      <el-col
        v-if="esCalculoPorGramaje"
        :span="2"
      >
        <custom-input-number
          v-model="model.GramajeResultante"
          label="Gramaje (g/m²)"
          :settings="{
            placeholder: 'g/m²',
            min: 0,
            precision: 2,
            step: 0.01,
            controls: false,
            inputClass: { 'input-error': faltaGramaje },
          }"
          @update:model-value="emitUpdate"
        />
      </el-col>
      <el-col
        :span="2"
        class="bobinas-col"
      >
        <el-button
          :type="faltanBobinas ? 'danger' : 'primary'"
          size="default"
          @click="emit('open-bobinas')"
          >Bobinas</el-button
        >
      </el-col>

      <el-col :span="2">
        <custom-input-number
          v-model="model.CantidadCapas"
          label="Capas/Tornillos"
          :settings="{ placeholder: 'Capas', min: 1, precision: 0, step: 1, controls: false }"
          @update:model-value="emitUpdate"
        />
      </el-col>

      <el-col :span="4">
        <div class="label">Tipo de extrusión</div>
        <el-select
          v-model="model.ExtrusionTipo"
          placeholder="Seleccionar"
          class="ancho_total"
          value-key="Codigo"
          size="default"
          @change="emitUpdate"
        >
          <el-option
            v-for="opt in ExtrusionTipoOptions"
            :key="opt.Codigo"
            :label="opt.Descripcion"
            :value="opt"
            :disabled="props.isOptionDisabled ? !!props.isOptionDisabled(opt) : false"
          />
        </el-select>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import {
  reactive,
  watch,
  computed,
  ref,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import ExtrusionTipoService from '@/services/tipo_extrusion_service';
import { MaterialService } from '@/services/material_service';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';

const props = defineProps({
  modelValue: { type: Object, required: true },
  isOptionDisabled: { type: Function, default: null },
  bobinas: { type: Array, default: () => [] },
  // Densidad estimada a partir de las resinas de cada tornillo, calculada por el padre
  // (que tiene el modelo completo del proceso). Fallback cuando el maestro no la trae.
  densidadResinas: { type: Number, default: null },
  // El proceso ya tiene tornillos/resinas cargados: cambiar el material resultante los pisa,
  // así que hay que avisarlo en el modal de confirmación.
  tieneFormulaCargada: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'open-bobinas', 'replace-formula']);

const { categoriaItemMaterialResultante, calculoMaterialExtrusion } =
  storeToRefs(useConfiguracionStore());

const esCalculoPorGramaje = computed(() => calculoMaterialExtrusion.value === 'gramaje');

const ExtrusionTipoOptions = ref([]);
const materialesExtrusion = ref([]);

onMounted(async () => {
  const response = await ExtrusionTipoService.findAll();
  if (response?.data) ExtrusionTipoOptions.value = response.data;

  if (materialesExtrusion.value.length === 0) {
    MaterialService.findAll({
      categoria: categoriaItemMaterialResultante.value,
      page: 0,
      limit: 200,
    })
      .then((res) => {
        materialesExtrusion.value = res?.data ?? [];
      })
      .catch(() => {});
  }
});

const model = reactive({
  ExtrusionTipo: null,
  MaterialResultante: null,
  CantidadCapas: null,
  EspesorResultante: null,
  GramajeResultante: null,
  DensidadResultante: null,
});

// Al leer un documento guardado, el ExtrusionTipo del backend puede venir sin la relación
// RecursosTipo (RelRecursoTipoExtrusionTipo), que el padre necesita para calcular el
// factor de multiplicación. Si falta, reconstruimos el objeto completo desde el catálogo.
const resolveExtrusionTipo = (val) => {
  if (val?.ExtrusionTipo?.RecursosTipo) return val.ExtrusionTipo;
  const codigo = val?.ExtrusionTipo?.Codigo ?? val?.CodigoExtrusionTipo ?? null;
  if (codigo == null) return null;
  return ExtrusionTipoOptions.value.find((o) => o.Codigo === codigo) ?? val?.ExtrusionTipo ?? null;
};

// MaterialResultante sí tiene relación en Prisma, pero por si el objeto no viene incluido
// (por ejemplo mientras se cargan las opciones), lo reconstruimos desde CodigoMaterialResultante.
const resolveMaterialResultante = (val) => {
  if (val?.MaterialResultante) return val.MaterialResultante;
  const codigo = val?.CodigoMaterialResultante ?? null;
  if (codigo == null) return null;
  return materialesExtrusion.value.find((m) => m.Codigo === codigo) ?? null;
};

watch(
  () => [props.modelValue, ExtrusionTipoOptions.value, materialesExtrusion.value],
  () => {
    const val = props.modelValue;
    if (!val) return;
    model.ExtrusionTipo = resolveExtrusionTipo(val);
    model.MaterialResultante = resolveMaterialResultante(val);
    model.CantidadCapas = val.CantidadCapas ?? null;
    model.EspesorResultante = val.EspesorResultante ?? null;
    model.GramajeResultante = val.GramajeResultante ?? null;
    model.DensidadResultante = val.DensidadResultante ?? null;
  },
  { immediate: true, deep: true },
);

const esValorCargado = (valor) =>
  valor !== null && valor !== undefined && valor !== '' && Number(valor) > 0;

// Campos que el cotizador necesita para extrusión: se marcan en rojo mientras estén vacíos,
// y la validación del documento bloquea el envío hasta que se completen.
const faltaMaterialResultante = computed(() => !model.MaterialResultante?.Codigo);
// El ancho del material sale de la suma de los anchos de las bobinas: sin bobinas no hay ancho.
const faltanBobinas = computed(
  () => !props.bobinas?.some((bobina) => esValorCargado(bobina?.Ancho)),
);
const faltaEspesor = computed(
  () => !esCalculoPorGramaje.value && !esValorCargado(model.EspesorResultante),
);
const faltaDensidad = computed(
  () => !esCalculoPorGramaje.value && !esValorCargado(model.DensidadResultante),
);
const faltaGramaje = computed(
  () => esCalculoPorGramaje.value && !esValorCargado(model.GramajeResultante),
);

/**
 * Valores a autocompletar para el material resultante elegido. Se toman del maestro de
 * materiales y, cuando ahí vienen nulos, se estiman: la densidad como promedio ponderado de
 * las resinas de cada tornillo, y el gramaje como Densidad x Espesor una vez resueltos esos
 * dos. El espesor no es derivable de las resinas — es una decisión del proceso — así que si
 * el maestro no lo trae queda vacío y lo reclama la validación al cotizar.
 */
const valoresAutocompletados = (material) => {
  const densidad = esValorCargado(material?.Densidad) ? material.Densidad : props.densidadResinas;
  const espesor = esValorCargado(material?.Espesor) ? material.Espesor : null;
  const gramaje = esValorCargado(material?.Gramaje)
    ? material.Gramaje
    : esValorCargado(densidad) && esValorCargado(espesor)
      ? Number((densidad * espesor).toFixed(2))
      : null;

  return { densidad, espesor, gramaje };
};

/** Fórmula del material resultante: define capas, tornillos y resinas de la mezcla. */
const obtenerFormula = async (codigoMaterial) => {
  try {
    const response = await MaterialService.findByCode(codigoMaterial);
    return response?.data?.MaterialFormula || [];
  } catch {
    return [];
  }
};

const onMaterialChange = async (material) => {
  if (!material) {
    emitUpdate();
    return;
  }

  const { densidad, espesor, gramaje } = valoresAutocompletados(material);

  // Según calculo_material_extrusion solo se autocompletan/comparan los campos del modo activo
  const campos = esCalculoPorGramaje.value
    ? [{ prop: 'GramajeResultante', label: 'Gramaje (g/m²)', nuevo: gramaje }]
    : [
        { prop: 'EspesorResultante', label: 'Espesor (μm)', nuevo: espesor },
        { prop: 'DensidadResultante', label: 'Densidad (g/cm³)', nuevo: densidad },
      ];

  // La estructura de tornillos ya cargada se pisa con la del material nuevo, pero solo si
  // ese material tiene fórmula: sin fórmula no hay con qué reemplazarla y se deja como está.
  const formulas = await obtenerFormula(material.Codigo);
  const reemplazaFormula = formulas.length > 0 && props.tieneFormulaCargada;

  const tieneValoresPrevios = campos.some((c) => model[c.prop] != null);

  // Nada que pisar: se autocompleta y el padre precarga la fórmula por su cuenta.
  if (!tieneValoresPrevios && !reemplazaFormula) {
    campos.forEach((c) => {
      model[c.prop] = c.nuevo;
    });
    emitUpdate();
    return;
  }

  try {
    const filas = campos
      .map(
        (c) => `<tr>
            <td style="padding: 4px 8px;">${c.label}</td>
            <td style="padding: 4px 8px;">${model[c.prop] ?? '-'}</td>
            <td style="padding: 4px 8px;">${c.nuevo ?? '-'}</td>
          </tr>`,
      )
      .join('');
    const avisoFormula = reemplazaFormula
      ? `<p style="margin-top: 12px;">También se reemplazarán <strong>la cantidad de capas, la proporción de cada tornillo,
           los materiales mezcla y las resinas de cada tornillo</strong> por los de la fórmula del material seleccionado.
           Se perderán los datos cargados actualmente.</p>`
      : '';
    await ElMessageBox.confirm(
      `<div style="font-size: 14px; line-height: 1.5; text-align: left;">
        <p>El material seleccionado tiene valores propios. ¿Desea reemplazar los valores actuales?</p>
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
          <tr>
            <th style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #ebeef5;"></th>
            <th style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #ebeef5;">Actual</th>
            <th style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #ebeef5;">Nuevo</th>
          </tr>
          ${filas}
        </table>
        ${avisoFormula}
      </div>`,
      'Reemplazar valores del material',
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: 'Reemplazar',
        cancelButtonText: 'Mantener valores actuales',
        type: 'warning',
      },
    );
    campos.forEach((c) => {
      model[c.prop] = c.nuevo;
    });
    emitUpdate();

    if (reemplazaFormula) {
      emit('replace-formula', { codigoMaterial: material.Codigo, formulas });
    }
    return;
  } catch {
    // Cancelado: se mantienen los valores previos (y la fórmula cargada)
  }

  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', {
    ...props.modelValue,
    ...model,
    CodigoExtrusionTipo: model.ExtrusionTipo?.Codigo ?? null,
    CodigoMaterialResultante: model.MaterialResultante?.Codigo ?? null,
  });
};

// Las resinas se suelen cargar después de elegir el material resultante, así que la densidad
// estimada llega más tarde que el autocompletado inicial. Se completa en cuanto está
// disponible, y solo si el campo sigue vacío: un valor cargado a mano nunca se pisa.
watch(
  () => [props.densidadResinas, esCalculoPorGramaje.value, model.EspesorResultante],
  () => {
    if (esCalculoPorGramaje.value) {
      if (esValorCargado(model.GramajeResultante)) return;
      if (!esValorCargado(props.densidadResinas) || !esValorCargado(model.EspesorResultante))
        return;
      model.GramajeResultante = Number(
        (props.densidadResinas * model.EspesorResultante).toFixed(2),
      );
      emitUpdate();
      return;
    }

    if (esValorCargado(model.DensidadResultante)) return;
    if (!esValorCargado(props.densidadResinas)) return;
    model.DensidadResultante = props.densidadResinas;
    emitUpdate();
  },
);
</script>

<style scoped>
.extrusion-proceso-form {
  margin-top: 10px;
  margin-bottom: 10px;
}
.label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
}
.ancho_total {
  width: 100%;
}

.bobinas-col {
  display: flex;
  justify-content: center;
}
</style>
